import { BillingModel } from '@futrli-sdk/app-store';
import { SubscriptionMeta } from '@futrli-sdk/billing';
import { ProductSetup } from '@futrli-sdk/organisations';
import { Permission, Workspace } from '@futrli-sdk/workspaces';
import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import { AxiosResponse } from 'axios';
import { APIClients } from '@futrli/shared-services';

/**
 * Gets the data from an AxiosResponse.
 * @param {getAxiosResponseData<T>} response - Gets an axios response and return the data.
 * @returns {T} - Response data.
 */
const getAxiosResponseData = <T>({ data }: AxiosResponse<T>): T => data;

/**
 * Gets the user permissions for a specific workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<Permission[]>}
 */
export const getUserPermissionsByWorkspace = async (workspaceId: string): Promise<Permission[]> => {
  const interpolated = interpolatePathParams(API_PATHS.WORKSPACE_PERMISSIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: interpolated
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Activates a product in an organisation
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productId
 * @param {BillingModel} billingModel
 * @returns {Promise<ProductSetup>}
 */
export const activateProduct = async (
  workspaceId: string,
  organisationId: string,
  productId: string,
  billingModel: BillingModel
): Promise<ProductSetup> => {
  const interpolated: string = interpolatePathParams(API_PATHS.PRODUCT_SETUP, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: interpolated,
        data: {
          product_id: productId,
          billing_model: billingModel
        }
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetchs the billing subscription metadata.
 * @param {string} workspaceId
 * @returns {Promise<SubscriptionMeta>}
 */
export const fetchBillingSubscriptionMeta = async (workspaceId: string): Promise<SubscriptionMeta> => {
  const interpolated: string = interpolatePathParams(API_PATHS.BILLING_META, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: interpolated
      }
    }
  });

  return getAxiosResponseData(response);
};

export const workspacesProvision = async (
  workspaceName: string | null,
  inviteSource: null | string,
  inviteReference: null | string,
  sage50BusinessId: null | string,
  emailAddress: string,
  businessType: string
): Promise<Workspace> => {
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: API_PATHS.WORKSPACES_PROVISION,
        data: {
          workspace_name: workspaceName,
          email_address: emailAddress,
          business_type: businessType,
          ...(inviteSource && { invite_source: inviteSource }),
          ...(inviteReference && { invite_reference: inviteReference }),
          ...(sage50BusinessId && { source_type: 'sage50' })
        }
      }
    }
  });
  return getAxiosResponseData(response.data);
};
